import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import {
  DatePickerFilter,
  FilterActionButton,
  FilterComponent,
  FilterItem,
  MonthPickerFilter,
  SelectFilter,
  YearPickerFilter,
} from '@h5s/design-system';
import { AstatDailyLocalTaxInfo, IstatType, Month, OwnerIstatProfile } from '@h5s/entities';
import { today } from '@h5s/util-date';
import { DateTime } from 'luxon';
import { FeatureIstatService } from '../../../feature-istat.service';
import { FeatureIstatStore } from '../../store/feature-istat.store';
import { FEATURE_ISTAT_TRANSLATIONS } from '../../translations/feature-istat.translations';
import { IstatFilterModel } from './models/istat-filter-model';

@Component({
  selector: 'h5s-istat-filter',
  imports: [ReactiveFormsModule, FilterComponent],
  templateUrl: './istat-filter.component.html',
  styleUrl: './istat-filter.component.scss',
})
export class IstatFilterComponent {
  private readonly featureIstatStore = inject(FeatureIstatStore);
  private readonly featureIstatService = inject(FeatureIstatService);
  private readonly destroyRef = inject(DestroyRef);

  /** Traduzioni */
  public readonly translation = FEATURE_ISTAT_TRANSLATIONS;

  /** Template */
  private readonly consolidateDialog = viewChild<TemplateRef<unknown>>('consolidateDialog');

  /** Outputs */
  public filterChange = output<IstatFilterModel>();

  /** Variabili */
  protected readonly selectedProfile = signal<OwnerIstatProfile | null>(null);
  protected readonly profileId = signal<number | null>(null);
  protected readonly year = signal<number | null>(null);
  protected readonly month = signal<number | null>(null);
  protected readonly day = signal<DateTime | null>(null);
  protected readonly beginSearchDate = signal<DateTime | null>(null);
  protected readonly endSearchDate = signal<DateTime | null>(null);
  protected readonly currentFilters = signal<IstatFilterModel | null>(null);

  public trackUserChangesEnabled = true;
  public readonly today = today();
  public readonly monthOptions = Month.getMonths();
  public profileOptions = this.featureIstatStore.ownerIstatProfileRecord;
  public selectedIstatType = this.featureIstatStore.selectedIstatType;

  // Valida la data di inizio del periodo selezionato
  public readonly maxStartDate = computed<DateTime | undefined>(() => this.endSearchDate() ?? undefined);

  // Valida la data di fine del periodo selezionato
  public readonly minEndDate = computed<DateTime | undefined>(() => this.beginSearchDate() ?? undefined);

  /** Configurazione dei campi del filtro */
  public readonly filterItems: FilterItem<IstatFilterModel>[] = [
    new SelectFilter({
      key: 'profile',
      label: this.translation.profileFilter,
      dataList: this.featureIstatStore.ownerIstatProfileRecord,
      getChipDisplayValue: (filterModel) => {
        const profile = filterModel['profile'];
        const options = this.profileOptions();
        return options && profile ? options[profile] : '';
      },
      isMainFilter: true,
      validators: [Validators.required],
      trackValue: this.profileId,
    }),

    new YearPickerFilter({
      key: 'year',
      label: this.translation.yearFilter,
      isMainFilter: true,
      trackValue: this.year,
      maxYear: this.today.year,
      validators: computed(() =>
        this.getOwnerIstatProfileById()?.istatType.monthly ? [Validators.required] : ([] as ValidatorFn[]),
      ),
      hide: computed(() => {
        return !this.getOwnerIstatProfileById()?.istatType.monthly;
      }),
    }),

    new MonthPickerFilter({
      key: 'month',
      label: this.translation.monthFilter,
      getChipDisplayValue: (filterModel) => {
        const month = filterModel['month'];
        const options = this.monthOptions;
        return options && month ? options[month] : '';
      },
      isMainFilter: true,
      trackValue: this.month,
      validators: computed(() =>
        this.getOwnerIstatProfileById()?.istatType.monthly ? [Validators.required] : ([] as ValidatorFn[]),
      ),
      hide: computed(() => !this.getOwnerIstatProfileById()?.istatType.monthly),
    }),

    new DatePickerFilter({
      key: 'day',
      label: this.translation.dayFilter,
      isMainFilter: true,
      trackValue: this.day,
      max: this.today,
      validators: computed(() =>
        this.getOwnerIstatProfileById()?.istatType.daily ? [Validators.required] : ([] as ValidatorFn[]),
      ),
      hide: computed(() => !this.getOwnerIstatProfileById()?.istatType.daily),
    }),

    new DatePickerFilter({
      key: 'startDate',
      label: this.translation.startDateFilter,
      isMainFilter: true,
      trackValue: this.beginSearchDate,
      max: this.maxStartDate,
      validators: computed(() =>
        this.getOwnerIstatProfileById()?.istatType.fromTo ? [Validators.required] : ([] as ValidatorFn[]),
      ),
      hide: computed(() => !this.getOwnerIstatProfileById()?.istatType.fromTo),
    }),

    new DatePickerFilter({
      key: 'endDate',
      label: this.translation.endDateFilter,
      isMainFilter: true,
      trackValue: this.endSearchDate,
      min: this.minEndDate,
      validators: computed(() =>
        this.getOwnerIstatProfileById()?.istatType.fromTo ? [Validators.required] : ([] as ValidatorFn[]),
      ),
      hide: computed(() => !this.getOwnerIstatProfileById()?.istatType.fromTo),
    }),
  ];

  /** Configurazione dei button del filtro */
  public readonly actionButtons: FilterActionButton[] = [
    {
      text: this.translation.buttonReportClosedStructure,
      color: 'accentSecondary',
      prefixIcon: 'uil uil-calendar-slash',
      click: () => this.reportClosedStructure(),
      hide: computed(() => {
        const istatType = this.getOwnerIstatProfileById()?.istatType;
        return !istatType?.sendFacilityClosure;
      }),
    },
    {
      text: this.translation.buttonCloseMonth,
      color: 'accentSecondary',
      prefixIcon: 'uil uil-file-check-alt',
      click: () => this.consolidatesIstatData(),
      hide: computed(() => {
        const istatType = this.getOwnerIstatProfileById()?.istatType;
        return !istatType?.sendIstatClosure;
      }),
    },
  ];

  constructor() {
    /**
     * Inizializzo la select dei profili Istat con il primo profilo disponibile
     */
    effect(() => {
      const filters = this.currentFilters();
      const profiles = this.profileOptions();
      if (!profiles || Object.keys(profiles).length === 0) return;

      const hasValidFilters = filters != null && Object.keys(filters).length > 0;

      const profileId = hasValidFilters && filters.profile != null ? filters.profile : Number(Object.keys(profiles)[0]);

      this.profileId.set(profileId);
    });

    /**
     * Gestione modifica del profilo Istat selezionato
     */
    effect(() => {
      const profileId = this.profileId();

      if (!profileId) return;
      const profile = this.featureIstatStore.getOwnerIstatProfileById(profileId);
      if (!profile) return;
      untracked(() => this.featureIstatStore.updateSelectedOwnerIstatProfile(profile));
    });

    /**
     * Gestione abilitazione/disabilitazione campi del form in base al tipo di Istat selezionato
     */
    effect(() => {
      const istatType = this.selectedIstatType();
      if (!istatType) return;

      const current = this.currentFilters() ?? {};

      // Costruisci i valori del form in base al tipo ISTAT
      const newValues = this.buildFormValues(current, undefined, istatType);

      // Aggiorna i signals dei trackValue
      this.year.set(newValues.year ?? null);
      this.month.set(newValues.month ?? null);
      this.day.set(newValues.day ?? null);
      this.beginSearchDate.set(newValues.startDate ?? null);
      this.endSearchDate.set(newValues.endDate ?? null);
    });
  }

  public onFiltersChange(filters: IstatFilterModel): void {
    this.currentFilters.set(filters);
  }

  /** Costruisce i valori del form in base al tipo di Istat selezionato */
  private buildFormValues(
    currentForm: Partial<IstatFilterModel>,
    filters?: IstatFilterModel,
    istatType?: IstatType,
  ): Partial<IstatFilterModel> {
    const today = this.today;

    const formValues: Partial<IstatFilterModel> = {
      profile: currentForm.profile ?? null,
      year: null,
      month: null,
      day: null,
      startDate: null,
      endDate: null,
    };

    if (filters) {
      formValues.profile = filters.profile ?? formValues.profile;
      formValues.year = filters.year ?? null;
      formValues.month = filters.month ?? null;
      formValues.day = filters.day ?? null;
      formValues.startDate = filters.startDate ?? null;
      formValues.endDate = filters.endDate ?? null;
    }

    if (istatType) {
      if (istatType.daily) {
        formValues.day = filters?.day ?? currentForm.day ?? today;
      }

      if (istatType.monthly) {
        formValues.month = filters?.month ?? currentForm.month ?? today.month;
        formValues.year = filters?.year ?? currentForm.year ?? today.year;
      }

      if (istatType.fromTo) {
        formValues.startDate = filters?.startDate ?? currentForm.startDate ?? today;
        formValues.endDate = filters?.endDate ?? currentForm.endDate ?? today;
        formValues.year = filters?.year ?? currentForm.year ?? today.year;
      }
    }

    return formValues;
  }

  /**
   * Gestisce il risultato della ricerca.
   * Emette i filtri al componente padre e riabilita il bottone scarica/invia ISTAT
   * dell'header che era stato disabilitato al cambio profilo.
   *
   * @param {IstatFilterModel} filters - I filtri con cui è stata effettuata la ricerca
   */
  protected onSearchChange(filters: IstatFilterModel): void {
    this.filterChange.emit(filters);
    this.featureIstatStore.updateIstatProfileIsChanged(false);
  }

  /**
   * Apre modale per segnalare struttura chiusa
   */
  public reportClosedStructure(): void {
    const profile = this.getOwnerIstatProfileById();
    if (!profile) return;

    this.featureIstatService
      .openReportClosedStructureModal(profile)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  /**
   * Apre modale per consolidare i dati Istat
   */
  public consolidatesIstatData(): void {
    const profile = this.getOwnerIstatProfileById();
    if (!profile) return;

    this.featureIstatService
      .handleConsolidateIstatData(profile, this.consolidateDialog())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private getOwnerIstatProfileById(): OwnerIstatProfile | undefined {
    const profileId = this.profileId();
    return profileId ? (this.featureIstatStore.getOwnerIstatProfileById(profileId) ?? undefined) : undefined;
  }

  /**
   * Dati di tipo AstatDailyLocalTaxInfo da visualizzare nel dialogo di consolidamento
   */
  public buildConsolidateIstatSummary(data: AstatDailyLocalTaxInfo): { label: string; value: number | string }[] {
    if (!data) return [];
    return [
      { label: this.translation.dialogConsolidateIstatLabels.year, value: data.year },
      { label: this.translation.dialogConsolidateIstatLabels.month, value: data.month },
      {
        label: this.translation.dialogConsolidateIstatLabels.monthClosed,
        value: data.monthClosed ? this.translation.yes : this.translation.no,
      },
      { label: this.translation.dialogConsolidateIstatLabels.totalArrivals, value: data.totalArrivals },
      { label: this.translation.dialogConsolidateIstatLabels.totalDepartures, value: data.totalDepartures },
      { label: this.translation.dialogConsolidateIstatLabels.totalGuestsPresent, value: data.totalGuestsPresent },
      { label: this.translation.dialogConsolidateIstatLabels.totalTaxAdults, value: data.totalTaxAdults },
      { label: this.translation.dialogConsolidateIstatLabels.totalTaxChildren, value: data.totalTaxChildren },
      { label: this.translation.dialogConsolidateIstatLabels.totalTaxExempted, value: data.totalTaxExempted },
    ];
  }
}
